import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { Storage } from '@ionic/storage-angular';

import { BehaviorSubject, from, map, Observable, switchMap } from 'rxjs';

import {
    ApiDataType,
    EnvironmentType,
    LoginApiResponse,
    PasswordResetRequestDTO,
    SubmittedNewPasswordDTO
} from '../../types';
import { EmailPasswordInputType, NewPasswordFormInputTypes } from '../../types/forms/input-fields';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private storage: Storage | null = null;
    private env: EnvironmentType;

    token: string | null = null;

    private tokenSubject = new BehaviorSubject<string | null>(null);
    token$ = this.tokenSubject.asObservable();

    public redirectRouter = '';

    forgotPasswordEmail: string | null = null;
    forgotPasswordCode: string = '';

    constructor(
        private http: HttpClient,
        @Inject('environment') environment: EnvironmentType,
        @Inject('storage') storage: Storage
    ) {
        this.env = environment;
        this.storage = storage;
    }

    setEnv(env: EnvironmentType) {
        this.env = { ...env };
    }

    login(obj: EmailPasswordInputType): Observable<LoginApiResponse> {
        const postObj = {
            email: obj.email,
            password: obj.password
        };
        return this.http.post<LoginApiResponse>(`${this.env.apiUrl}/login`, postObj).pipe(
            map(data => {
                if (data.success) {
                    this.setToken(data.data.token);
                } else {
                    this.setToken(null);
                }
                return data;
            })
        );
    }

    logout(): Observable<ApiDataType> {
        const headers = new HttpHeaders().set('Authorization', this.tokenSubject.value || '');

        return this.http
            .post<ApiDataType>(
                `${this.env.apiUrl}/logout`,
                {},
                {
                    headers: headers
                }
            )
            .pipe(
                map(data => {
                    if (data.success) {
                        this.token = null;
                        this.setToken(null);

                        if (this.storage) {
                            this.storage.clear();
                        }
                    }
                    return data;
                })
            );
    }

    setToken(token: string | null) {
        this.tokenSubject.next(token);
    }

    getCachedToken() {
        return this.tokenSubject.value;
    }

    tokenExists(): Observable<boolean> | void {
        if (!this.storage) return;
        return from(this.storage.get(this.env.tokenKey)).pipe(
            map(token => {
                if (token) {
                    return true;
                } else {
                    return false;
                }
            })
        );
    }

    getToken(): Observable<unknown> | void {
        if (!this.storage) return;
        return from(this.storage.get(this.env.tokenKey)).pipe(
            switchMap(token => {
                return token;
            })
        );
    }

    saveToken(token: string) {
        if (!this.storage) return;
        this.storage.set(this.env.tokenKey, token).then(() => {
            // console.log('saved token');
            this.setToken(token);
            this.token = token;
        });
    }

    clearToken() {
        if (!this.storage) return;
        this.storage.remove(this.env.tokenKey).then(() => {
            // console.log('removed token');
            this.token = null;
            this.setToken(null);
        });
    }

    setRedirectRouterUrl(url: string) {
        this.redirectRouter = url;
    }

    getRedirectRouterUrl() {
        return this.redirectRouter;
    }

    setForgotPasswordEmail(value: string | null) {
        console.log(value);
        this.forgotPasswordEmail = value;
    }

    getForgotPasswordEmail() {
        console.log(this.forgotPasswordEmail);
        return this.forgotPasswordEmail;
    }

    setForgotPasswordCode(value: string) {
        this.forgotPasswordCode = value;
    }

    getForgotPasswordCode() {
        return this.forgotPasswordCode;
    }

    requestPasswordReset(email: string): Observable<PasswordResetRequestDTO> {
        return this.http.post<PasswordResetRequestDTO>(`${this.env.apiUrl}/password-reset/request`, {
            email: email
        });
    }

    newPassword(obj: NewPasswordFormInputTypes): Observable<SubmittedNewPasswordDTO> {
        const formObj = {
            email: obj.email,
            code: obj.code,
            password: obj.password,
            password_confirmation: obj.password_confirmation
        };

        return this.http.post<SubmittedNewPasswordDTO>(`${this.env.apiUrl}/password-reset/submit`, formObj);
    }
}
